import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { Switch } from '@mui/material';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  MaritimeDirectoryPortResponse,
  UPDATE_MARITIME_DIRECTORY_PORT
} from '../../api/graphql/maritimeDirectoryPort/maritimeDirectoryPort.mutations';
import { counterPartyModel, CounterPartyType, CounterPartyTypeSingularLabels, maritimeDirectoryPortModel } from 'model';
import { useApolloErrorHandler } from 'hooks';
import capitalizeEveryWord from '../../../../../shared/stringUtils/capitalizeEveryWord';
import {
  TXT_ADD,
  TXT_ARE_YOU_SURE_YOU_WANT_TO_ADD_REMOVE_COUNTERPARTY_FOR_PORT,
  TXT_REMOVE,
  TXT_UPDATE_PORT
} from '../../../../../shared/translations';
import capitalize from '../../../../../shared/stringUtils/capitalize';

interface MaritimeDirectoryPortIsActiveProps {
  counterPartyType: CounterPartyType;
  counterParty: Partial<counterPartyModel>;
  maritimeDirectoryPort: Partial<maritimeDirectoryPortModel>;
  onUpdated?: (isActive: boolean) => void;
}

const MaritimeDirectoryPortIsActive = (props: MaritimeDirectoryPortIsActiveProps) => {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(props.maritimeDirectoryPort.isActive || false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const tempValue = useRef<boolean | null>(null);
  const { apolloErrorHandler } = useApolloErrorHandler();

  const [save, { loading }] = useMutation<MaritimeDirectoryPortResponse>(UPDATE_MARITIME_DIRECTORY_PORT, {
    variables: {
      input: {
        id: props.maritimeDirectoryPort.id || '',
        isActive: tempValue.current || false
      }
    },
    onError: apolloErrorHandler,
    onCompleted: (data) => {
      if (!data.maritimeDirectoryPort) {
        return;
      }
      setIsActive(data.maritimeDirectoryPort.isActive || false);
      tempValue.current = null;
      toggleConfirmation();
      if (props.onUpdated) {
        props.onUpdated(data.maritimeDirectoryPort.isActive || false);
      }
    }
  });

  const toggleConfirmation = useCallback(() => {
    setShowConfirmation((prev) => !prev);
  }, []);

  const onChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      tempValue.current = checked;
      toggleConfirmation();
    },
    [toggleConfirmation]
  );

  return (
    <>
      {showConfirmation ? (
        <ConfirmationDialog
          title={capitalizeEveryWord(t(TXT_UPDATE_PORT))}
          open
          onCanceled={toggleConfirmation}
          onAccepted={save}
          loading={loading}
        >
          {capitalize(
            t(TXT_ARE_YOU_SURE_YOU_WANT_TO_ADD_REMOVE_COUNTERPARTY_FOR_PORT, {
              addOrRemove: t(isActive ? TXT_REMOVE : TXT_ADD),
              counterParty: props.counterParty.name || '',
              type: CounterPartyTypeSingularLabels[props.counterPartyType],
              port: props.maritimeDirectoryPort.port?.name || ''
            })
          )}
        </ConfirmationDialog>
      ) : null}
      <Switch checked={isActive} onChange={onChange} />
    </>
  );
};

export default MaritimeDirectoryPortIsActive;
